import AIRadarChart from "@hireroo/charts/react/v2/AIRadarChart";
import { useTranslation } from "@hireroo/i18n";
import AiGeniusIcon from "@hireroo/ui-assets/images/Remote/AiGenius.svg";
import AiRoolarWinkIcon from "@hireroo/ui-assets/images/Remote/AiRoolarWink.svg";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarIcon from "@mui/icons-material/Star";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import * as React from "react";

import FeedbackItem, { FeedbackItemProps } from "../../modules/FeedbackSummary/parts/FeedbackItem/FeedbackItem";
import Link from "../../primitive/Link/Link";

type SkillMetric = {
  name: string;
  stars: number;
  comment: string;
};

export type AiEvaluationProps = {
  /**
   * 評価日時
   */
  evaluatedAt: string;
  /**
   * スキルメトリクスの配列
   */
  skills: SkillMetric[];
  /**
   * 総評
   */
  totalEvaluation: string;
  averageStar: number;
  /**
   * フォールバックメッセージ
   */
  fallbackMessage?: string;
  /**
   * ヘルプリンク
   */
  helpLink?: {
    text: string;
    href: string;
  };
};

const StyledIconWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  "& img": {
    width: "100%",
    height: "100%",
    filter: "brightness(0) invert(1)", // SVG画像を白色に変換
  },
}));

const StyledCardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(2),
  backgroundColor: theme.palette["Secondary/Shades"].p8,
}));

const StyledFeedbackItemWrapper = styled(Box)(() => ({
  "& > div > div:first-of-type > p": {
    fontSize: "0.875rem", // body2のフォントサイズ
    fontWeight: 700,
  },
}));

const AiEvaluation: React.FC<AiEvaluationProps> = props => {
  const { evaluatedAt, skills, totalEvaluation } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const feedbackItems = React.useMemo((): FeedbackItemProps[] => {
    return skills.map((skill, index) => ({
      id: `skill-${index}`,
      title: skill.name,
      evaluation: {
        numStars: skill.stars,
        comment: skill.comment,
      },
      disabled: skill.stars === 0,
    }));
  }, [skills]);

  // レーダーチャート用のデータを生成
  const radarData = React.useMemo(() => {
    return skills.map(skill => ({
      subject: skill.name,
      value: skill.stars,
      fullMark: 4, // 最大4つ星
    }));
  }, [skills]);

  return (
    <Stack spacing={2}>
      <StyledCardHeader>
        <StyledIconWrapper>
          <img src={AiGeniusIcon} alt="AI Genius" />
        </StyledIconWrapper>
        <Typography variant="body1" fontWeight={700}>
          {t("AI評価")}
        </Typography>
      </StyledCardHeader>

      <StyledCard>
        {props.fallbackMessage ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Avatar sx={{ width: 40, height: 40 }} src={AiRoolarWinkIcon} />
                <Stack spacing={2} sx={{ width: "100%" }}>
                  <Stack spacing={0.5}>
                    <Typography variant="body1" fontWeight={700} color="white">
                      {/* TODO: change name */}
                      Roolar
                    </Typography>
                  </Stack>
                  <Box sx={{ display: "flex" }}>
                    <Box>
                      <Typography fontSize={14} color="textPrimary">
                        {props.fallbackMessage}
                      </Typography>
                      {props.helpLink && (
                        <Box mt={1} display="flex" alignItems="center" fontSize={14}>
                          <Link href={props.helpLink.href} target="_blank" color="secondary">
                            {props.helpLink.text}
                            <OpenInNewIcon sx={{ ml: 0.5, fontSize: 18, verticalAlign: "middle" }} />
                          </Link>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Avatar sx={{ width: 40, height: 40 }} src={AiRoolarWinkIcon} />
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Stack spacing={0.5}>
                  <Typography variant="body1" fontWeight={700} color="white">
                    Roolar
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {evaluatedAt}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <StarIcon sx={{ color: theme.palette.warning.main }} />
                  <Typography fontWeight={700} color={theme.palette.warning.main}>
                    {props.averageStar.toFixed(1)}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography fontSize={14} color="textSecondary" sx={{ wordBreak: "keep-all" }}>
                  {t("総評")}:
                </Typography>
                <Typography fontSize={14} color="textPrimary">
                  {totalEvaluation}
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Grid container spacing={1}>
                  {/* 左側：スキル評価カード */}
                  <Grid item xs={12} md={9}>
                    <Grid container spacing={1.5}>
                      {feedbackItems.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <StyledFeedbackItemWrapper>
                            <FeedbackItem {...item} />
                          </StyledFeedbackItemWrapper>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* 右側：レーダーチャート */}
                  <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <React.Suspense fallback={<Skeleton variant="rectangular" height={260} />}>
                      <AIRadarChart data={radarData} name="スキル評価" />
                    </React.Suspense>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Stack>
        )}
      </StyledCard>
    </Stack>
  );
};

AiEvaluation.displayName = "AiEvaluation";

export default AiEvaluation;
